import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('lekeewallet_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('lekeewallet_token');
      localStorage.removeItem('lekeewallet_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  verifyPin: (pin) => api.post('/auth/verify-pin', { pin }),
  setPin: (pin) => api.post('/auth/set-pin', { pin }),
};

// Wallet
export const walletApi = {
  getWallet: () => api.get('/wallet'),
  getTransactions: (params) => api.get('/wallet/transactions', { params }),
  getTransaction: (id) => api.get(`/wallet/transactions/${id}`),
};

// Transfers
export const transferApi = {
  internal: (data) => api.post('/transfers/internal', data),
  bank: (data) => api.post('/transfers/bank', data),
  mobileMoney: (data) => api.post('/transfers/mobile-money', data),
};

// Withdrawal
export const withdrawalApi = {
  getMethods: () => api.get('/withdrawal/methods'),
  addMethod: (data) => api.post('/withdrawal/methods', data),
  getFees: (amount, methodId) => api.get('/withdrawal/fees', { params: { amount, method_id: methodId } }),
  request: (data) => api.post('/withdrawal/request', data),
};

// Campaigns
export const campaignApi = {
  getAll: (params) => api.get('/campaigns', { params }),
  getOne: (id) => api.get(`/campaigns/${id}`),
  getProgress: (id) => api.get(`/campaigns/${id}/progress`),
  accept: (id) => api.post(`/campaigns/${id}/accept`),
};

// Earnings
export const earningsApi = {
  getSummary: () => api.get('/earnings/summary'),
  getChart: (period) => api.get('/earnings/chart', { params: { period } }),
};

// Notifications
export const notificationApi = {
  getAll: (limit) => api.get('/notifications', { params: { limit } }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Receive
export const receiveApi = {
  getDetails: () => api.get('/receive/details'),
};

// Beneficiaries
export const beneficiaryApi = {
  getAll: () => api.get('/beneficiaries'),
  resolveAccount: (bankCode, accountNumber) => 
    api.get('/beneficiaries/resolve-account', { params: { bank_code: bankCode, account_number: accountNumber } }),
};

// KYC
export const kycApi = {
  getStatus: () => api.get('/kyc/status'),
  submit: (data) => api.post('/kyc/submit', data),
};

// Security
export const securityApi = {
  changePin: (oldPin, newPin) => 
    api.post('/security/change-pin', null, { params: { old_pin: oldPin, new_pin: newPin } }),
  enableBiometric: () => api.post('/security/enable-biometric'),
};

// Utils
export const utilsApi = {
  getBanks: () => api.get('/banks'),
  getMobileMoneyProviders: () => api.get('/mobile-money-providers'),
};

// Demo
export const demoApi = {
  getUsers: () => api.get('/demo/users'),
  getUserProfile: (walletId) => api.get(`/demo/users/${walletId}/profile`),
  loginAs: (walletId) => api.post(`/demo/login-as/${walletId}`),
  getCampaignNetwork: () => api.get('/demo/campaign-network'),
};

// Clips
export const clipsApi = {
  getAll: (params) => api.get('/clips', { params }),
  getMy: () => api.get('/clips/my'),
  getEarnings: () => api.get('/clips/earnings'),
  getCampaigns: () => api.get('/clips/campaigns'),
};

// Tips
export const tipsApi = {
  send: (to_wallet_id, amount, message, content_type) =>
    api.post('/tips/send', null, { params: { to_wallet_id, amount, message, content_type } }),
  getReceived: (limit) => api.get('/tips/received', { params: { limit } }),
  getSent: (limit) => api.get('/tips/sent', { params: { limit } }),
  getLeaderboard: () => api.get('/tips/leaderboard'),
  getCreators: () => api.get('/tips/creators'),
};

// Subscriptions
export const subscriptionsApi = {
  getPlans: () => api.get('/subscriptions/plans'),
  getMy: () => api.get('/subscriptions/my'),
  getSubscribers: () => api.get('/subscriptions/subscribers'),
  subscribe: (planId) => api.post('/subscriptions/subscribe', null, { params: { plan_id: planId } }),
  cancel: (subId) => api.post(`/subscriptions/${subId}/cancel`),
};

// Marketplace
export const marketplaceApi = {
  getBriefs: (category) => api.get('/marketplace/briefs', { params: { category } }),
  getBriefDetail: (id) => api.get(`/marketplace/briefs/${id}`),
  apply: (brief_id, pitch, proposed_rate) =>
    api.post('/marketplace/apply', null, { params: { brief_id, pitch, proposed_rate } }),
  getMyApplications: () => api.get('/marketplace/my-applications'),
  getCategories: () => api.get('/marketplace/categories'),
};

// Admin
export const adminApi = {
  login: (email, password) => 
    api.post('/admin/login', null, { params: { email, password } }),
  getDashboard: () => api.get('/admin/dashboard'),
  getUsers: (params) => api.get('/admin/users', { params }),
  getUserDetail: (id) => api.get(`/admin/users/${id}`),
  getCampaigns: (params) => api.get('/admin/campaigns', { params }),
  createCampaign: (data) => api.post('/admin/campaigns', data),
  updateCampaign: (id, data) => api.put(`/admin/campaigns/${id}`, data),
  getPayouts: (params) => api.get('/admin/payouts', { params }),
  approvePayout: (id) => api.post(`/admin/payouts/${id}/approve`),
  rejectPayout: (id, reason) => api.post(`/admin/payouts/${id}/reject`, null, { params: { reason } }),
  getKYCQueue: () => api.get('/admin/kyc/queue'),
  approveKYC: (id) => api.post(`/admin/kyc/${id}/approve`),
  rejectKYC: (id, reason) => api.post(`/admin/kyc/${id}/reject`, { action: 'reject', reason }),
  getTransactions: (params) => api.get('/admin/transactions', { params }),
  getFraudAlerts: () => api.get('/admin/fraud/alerts'),
  resolveFraudAlert: (id, notes) => api.post(`/admin/fraud/alerts/${id}/resolve`, null, { params: { notes } }),
};

export default api;
